/**
 * Braindump (Roadmap F-13, F-15): Gedanken als einzelne Eintraege eines
 * Journaltags oder einer Wochen-/Monatsreflexion. Gemerkte Gedanken
 * erscheinen zusaetzlich in "Gemerkte Gedanken", in der Inbox (F-48) und in
 * „Notizen & Gedanken" - es ist derselbe Eintrag, keine Kopie.
 */
import { db } from '@/db/schema';
import { createTask } from '@/db/repositories/tasks';
import { createProject } from '@/db/repositories/projects';
import { createGoal } from '@/db/repositories/goals';
import { inDenPapierkorb, rueckgaengigMachbar } from '@/db/repositories/papierkorb';
import { yearPeriod } from '@/domain/goals/period';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import { quelleGueltig } from '@/domain/journal/thoughts';
import type { GedankenQuelle, GedankenStatus, ID, LocalDate, Thought } from '@/domain/types';

/** `quelle` fehlt fuer Journal-Gedanken; bei einer Reflexion gehoert er dorthin. */
export async function addThought(date: LocalDate, text: string, quelle?: GedankenQuelle): Promise<Thought | null> {
  const t = text.trim();
  if (!t || !date) return null;
  if (quelle && !quelleGueltig(quelle)) return null;
  const ts = nowInstant();
  const g: Thought = {
    id: newId(), date, text: t, gemerkt: 0, status: 'neu', createdAt: ts, updatedAt: ts,
    ...(quelle ? { quelle: { art: quelle.art, key: quelle.key, ...(quelle.titel ? { titel: quelle.titel.slice(0, 200) } : {}) } } : {}),
  };
  await db.thoughts.add(g);
  return g;
}

export async function updateThought(id: ID, patch: Partial<Pick<Thought, 'text' | 'status' | 'note'>>): Promise<void> {
  await db.thoughts.where('id').equals(id).modify((g) => {
    // Ein leerer Text loescht den Gedanken nicht versehentlich - er wird ignoriert.
    if (patch.text?.trim()) g.text = patch.text.trim();
    if (patch.status) g.status = patch.status;
    if (patch.note !== undefined) {
      if (patch.note.trim()) g.note = patch.note;
      else delete g.note;
    }
    g.updatedAt = nowInstant();
  });
}

/**
 * Status setzen. "Erledigt / verworfen" ist rueckgaengig machbar (F-45) -
 * der Gedanke verschwindet damit aus der Inbox (F-48).
 */
export async function setGedankenStatus(id: ID, status: GedankenStatus): Promise<void> {
  if (status !== 'erledigt') return updateThought(id, { status });
  await rueckgaengigMachbar({ art: 'erledigt', tabelle: 'thoughts', zeileIds: [id] }, () => updateThought(id, { status }), [db.thoughts]);
}

/**
 * "Fuer spaeter merken" an/aus. Das Aufheben ist rueckgaengig machbar (F-45),
 * denn der Gedanke verschwindet damit aus Inbox, Journal-Karte und
 * „Notizen & Gedanken" (F-48).
 */
export async function toggleMerken(id: ID): Promise<void> {
  const g = await db.thoughts.get(id);
  if (!g) return;
  const umschalten = () => db.thoughts.where('id').equals(id).modify((x) => {
    x.gemerkt = x.gemerkt ? 0 : 1;
    x.updatedAt = nowInstant();
  });
  if (!g.gemerkt) { await umschalten(); return; }
  await rueckgaengigMachbar({ art: 'entfernt', tabelle: 'thoughts', zeileIds: [id] }, umschalten, [db.thoughts]);
}

export async function deleteThought(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'thoughts', zeileIds: [id] }, () => db.thoughts.delete(id));
}

/**
 * Weiterverarbeiten (Vorschlag): Aufgabe, Projekt oder Ziel aus dem Gedanken,
 * optional mit Bereich. Der Gedanke merkt sich das Ergebnis und gilt als
 * erledigt; die Notiz wandert als Beschreibung mit. Rueckgaengig machbar
 * (F-45), denn danach verschwindet er aus der Inbox (F-48).
 */
export function thoughtUmwandeln(id: ID, art: 'aufgabe' | 'projekt' | 'ziel', areaId: ID | null = null): Promise<ID | null> {
  return rueckgaengigMachbar({ art: 'umgewandelt', tabelle: 'thoughts', zeileIds: [id] }, () => umwandeln(id, art, areaId));
}

async function umwandeln(id: ID, art: 'aufgabe' | 'projekt' | 'ziel', areaId: ID | null): Promise<ID | null> {
  const g = await db.thoughts.get(id);
  if (!g) return null;
  if (g.umgewandelt) return g.umgewandelt.id;
  let neu: ID;
  if (art === 'aufgabe') {
    neu = (await createTask({ title: g.text, status: 'naechste', areaId, ...(g.note ? { notes: g.note } : {}) })).id;
  } else if (art === 'projekt') {
    neu = (await createProject({ title: g.text, areaId, ...(g.note ? { description: g.note } : {}) })).id;
  } else {
    const jahr = yearPeriod(Number(g.date.slice(0, 4)));
    neu = (await createGoal({ title: g.text, horizon: 'jahr', periodStart: jahr.start, periodEnd: jahr.end, areaId, ...(g.note ? { why: g.note } : {}) })).id;
  }
  await db.thoughts.update(id, { umgewandelt: { art, id: neu }, status: 'erledigt' as GedankenStatus, updatedAt: nowInstant() });
  return neu;
}
